import React, {
  useState,
  useEffect,
  useMemo,
  memo,
  useCallback,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaTags,
  FaUsers,
  FaClipboardList,
  FaChartBar,
  FaNewspaper,
  FaBullhorn,
  FaStore,
  FaComment,
  FaGraduationCap,
  FaClipboard,
} from "react-icons/fa";
import { Menu, X, ChevronLeft, ChevronRight, Building2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { selectUser, selectUserRole } from "../../store/slices/authSlice";
import { ROLES } from "../../api/constants";
// import AuthService from "../../services/authService";
import { useThemeContext } from "../ThemeProvider";
import { useGetTenantInfoQuery } from "../../services/tenantApi";
import useAuth from "../../hooks/useAuth";
import { MODULE_DEFINITIONS } from "../../constants/modules";

// Constants
const SIDEBAR_WIDTH = {
  EXPANDED: 280,
  COLLAPSED: 72,
};

const DEFAULT_LOGO = "/images/v3-Bravioo/PNG/Bravioo_logotype-white.png";
const DEFAULT_AVATAR =
  "https://cdn.pixabay.com/photo/2016/08/08/09/17/avatar-1577909_1280.png";
const DEFAULT_LOGO_MOBILE = "/images/v3-Bravioo/PNG/Bravioo_logo.png";

// 🚀 UNIFIED MENU CONFIGURATION - Single Source of Truth
// Tüm menü öğeleri tek bir listede, roller ve yetkiler belirtilerek
const ALL_MENU_ITEMS = [
  // Dashboard - Tüm roller için
  {
    id: "dashboard",
    label: "dashboard",
    icon: FaClipboard,
    link: "/dashboard",
    roles: [ROLES.ADMIN, ROLES.TENANT_ADMIN, ROLES.USER],
    order: 0, // Menüde sıralama için
  },

  // --- ORTAK MODÜLLER (Admin, TenantAdmin, User) ---
  {
    id: "campaignManagement",
    label: "campaignManagement",
    icon: FaNewspaper,
    link: "/user/campaigns",
    adminLink: "/admin/campaigns",
    requiredModule: "campaigns",
    requiredPermissions: ["campaigns.read"], // User için
    roles: [ROLES.ADMIN, ROLES.TENANT_ADMIN, ROLES.USER],
    order: 1,
  },
  {
    id: "drawsManagement",
    label: "drawsManagement",
    icon: FaTags,
    link: "/user/draws",
    adminLink: "/admin/draws",
    requiredModule: "draws",
    requiredPermissions: ["draws.read"],
    roles: [ROLES.ADMIN, ROLES.TENANT_ADMIN, ROLES.USER],
    order: 2,
  },
  {
    id: "announcements",
    label: "announcements",
    icon: FaBullhorn,
    link: "/user/announcement-management",
    adminLink: "/admin/announcement-management",
    requiredModule: "announcements",
    requiredPermissions: ["announcements.read"],
    roles: [ROLES.ADMIN, ROLES.TENANT_ADMIN, ROLES.USER],
    order: 3,
  },
  {
    id: "surveyManagement",
    label: "surveyManagement",
    icon: FaClipboardList,
    link: "/user/survey-management",
    adminLink: "/admin/survey-management",
    requiredModule: "surveys",
    requiredPermissions: ["surveys.read"],
    roles: [ROLES.ADMIN, ROLES.TENANT_ADMIN, ROLES.USER],
    order: 4,
  },
  {
    id: "brandManagement",
    label: "brandManagement",
    icon: FaStore,
    link: "/user/brand-management",
    adminLink: "/admin/brand-management",
    requiredModule: "brands",
    requiredPermissions: ["brands.read"],
    roles: [ROLES.ADMIN, ROLES.TENANT_ADMIN, ROLES.USER],
    order: 5,
  },
  {
    id: "ticketsManagement",
    label: "ticketsManagement",
    icon: FaComment,
    link: "/user/tickets-management",
    adminLink: "/admin/tickets-management",
    requiredModule: "tickets",
    requiredPermissions: ["tickets.read"],
    roles: [ROLES.ADMIN, ROLES.TENANT_ADMIN, ROLES.USER],
    order: 6,
  },
  {
    id: "videosManagement",
    label: "videosManagement",
    icon: FaGraduationCap,
    link: "/user/videos",
    adminLink: "/admin/videos",
    requiredModule: "videos",
    requiredPermissions: ["videos.read"],
    roles: [ROLES.ADMIN, ROLES.TENANT_ADMIN, ROLES.USER],
    order: 7,
  },

  // --- SADECE ADMIN İÇİN ---
  {
    id: "companyManagement",
    label: "companyManagement",
    icon: FaUsers,
    link: "/admin/company-management",
    requiredModule: "subTenants",
    roles: [ROLES.ADMIN],
    order: 8,
  },

  // --- TENANT_ADMIN VE USER İÇİN ---
  {
    id: "subTenantsManagement",
    label: "subsidiariesManagement",
    icon: Building2,
    link: "/user/company-subTenants",
    requiredModule: "subTenants",
    requiredPermissions: ["subTenants.read"],
    roles: [ROLES.TENANT_ADMIN, ROLES.USER],
    order: 9,
  },
  {
    id: "userManagement",
    label: "userManagement",
    icon: FaUsers,
    link: "/user/users",
    requiredModule: "users",
    requiredPermissions: ["users.read"],
    roles: [ROLES.TENANT_ADMIN, ROLES.USER],
    order: 10,
  },

  // Reports - Tüm roller için
  {
    id: "reports",
    label: "reports",
    icon: FaChartBar,
    link: "/user/reports",
    adminLink: "/admin/reports",
    requiredModule: "reports",
    requiredPermissions: ["reports.read"],
    roles: [ROLES.ADMIN, ROLES.TENANT_ADMIN, ROLES.USER],
    order: 11,
  },
];

// Responsive footer branding component
const FooterBranding = memo(({ isCollapsed, isMobile, primaryColor }) => {
  const currentYear = new Date().getFullYear();

  return (
    <motion.div
      className="w-full mt-auto px-4 py-4"
      layout
      transition={{ duration: 0.2 }}
    >
      <div
        className={`flex items-center ${
          isCollapsed && !isMobile ? "justify-center" : "justify-between"
        }`}
      >
        <div
          className={`flex items-center ${
            isCollapsed && !isMobile ? "" : "gap-3"
          }`}
        >
          <img
            src={isCollapsed && !isMobile ? DEFAULT_LOGO_MOBILE : DEFAULT_LOGO}
            alt="Logo"
            className={`${
              isCollapsed && !isMobile ? "h-6 w-6" : "h-8"
            } object-contain opacity-80`}
            draggable={false}
          />
          {!isCollapsed && (
            <div className="hidden sm:block">
              <p className="text-[11px] leading-tight font-medium text-white/90">
                © {currentYear} BRAVIOO
              </p>
              <p className="text-[10px] leading-tight text-white/70">
                Powered by DOTNET
              </p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
});

FooterBranding.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  primaryColor: PropTypes.string.isRequired,
};

// Utility function to add opacity to hex colors
const hexToRgba = (hex, alpha = 1) => {
  if (!hex || !hex.startsWith("#")) return `rgba(30, 64, 175, ${alpha})`; // #1E40AF default
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
// Professional Logo Component - Minimal design
const SidebarLogo = memo(({ isCollapsed, user }) => {
  const [logoError, setLogoError] = useState(false);
  const [logoLoading, setLogoLoading] = useState(true);
  const [logoKey, setLogoKey] = useState(0); // Force re-render key

  // Check if user is admin (who needs special handling)
  const isAdmin = user?.role === ROLES.ADMIN;

  // For admin: always fetch mytenant info
  // For tenant admin: use tenantId
  const shouldFetchTenantInfo = isAdmin || !!user?.tenantId;

  // Dinamik tema verilerini al - admin için tenantId'siz çağır
  const { visualIdentity, refreshTheme } = useThemeContext(
    isAdmin ? null : user?.tenantId,
    { skipApiCall: !shouldFetchTenantInfo }
  );

  // Admin için direkt mytenant endpoint'ini kullan
  // Tenant admin için normal tenantId'li endpoint
  const { data: tenantData, refetch: refetchTenant } = useGetTenantInfoQuery(
    undefined, // mytenant endpoint için parameter gerekmiyor
    {
      skip: !shouldFetchTenantInfo,
      refetchOnMountOrArgChange: 10, // More aggressive refetch
    }
  );

  // Track user changes for cache clearing
  const previousUser = useRef(user);
  const previousLogo = useRef(null);

  // Handle user change - force logo refresh
  useEffect(() => {
    const userChanged =
      previousUser.current?.email !== user?.email ||
      previousUser.current?.role !== user?.role ||
      previousUser.current?.tenantId !== user?.tenantId;

    if (userChanged) {
      previousUser.current = user;

      // Reset logo state completely
      setLogoError(false);
      setLogoLoading(true);
      setLogoKey((prev) => prev + 1); // Force re-render
      previousLogo.current = null;

      // Force refresh theme and tenant data
      if (refreshTheme) {
        refreshTheme();
      }
      if (refetchTenant) {
        refetchTenant();
      }
    }
  }, [user, refreshTheme, refetchTenant]);

  const logoUrl = useMemo(() => {
    let logo = null;

    // Önce visualIdentity'den dene
    if (visualIdentity?.logo) {
      logo = visualIdentity.logo;
    }
    // Yoksa tenant data'dan dene
    else if (tenantData?.value?.logo) {
      logo = tenantData.value.logo;
    }

    // If logo changed, reset error state
    if (logo !== previousLogo.current) {
      previousLogo.current = logo;
      setLogoError(false);
      setLogoLoading(true);
      setLogoKey((prev) => prev + 1); // Force re-render
    }

    if (logo) {
      // Add cache-busting parameter for reliable refresh
      const userIdentifier = isAdmin ? "admin" : user?.tenantId || "default";
      const cacheBuster = `?v=${Date.now()}&user=${userIdentifier}`;
      const logoWithCacheBuster = logo.includes("?")
        ? `${logo}&cb=${Date.now()}`
        : `${logo}${cacheBuster}`;

      return logoWithCacheBuster;
    }

    return DEFAULT_LOGO;
  }, [
    logoError,
    visualIdentity?.logo,
    tenantData?.value?.logo,
    user?.tenantId,
    isAdmin,
    shouldFetchTenantInfo,
  ]);

  const companyName = useMemo(() => {
    return visualIdentity?.name || tenantData?.value?.name || "Company";
  }, [visualIdentity?.name, tenantData?.value?.name]);

  const handleLogoLoad = useCallback(() => {
    setLogoLoading(false);
  }, [logoUrl]);

  const handleLogoError = useCallback(() => {
    setLogoError(true);
    setLogoLoading(false);
  }, [logoUrl]);

  // Force logo refresh when visual identity changes
  useEffect(() => {
    if (visualIdentity?.logo && logoError) {
      setLogoError(false);
      setLogoLoading(true);
      setLogoKey((prev) => prev + 1);
    }
  }, [visualIdentity?.logo, logoError]);

  return (
    <motion.div
      className="flex items-center justify-center px-4 py-4 border-b border-white/10"
      layout
      transition={{ duration: 0.3, ease: "easeInOut" }}
    >
      <AnimatePresence mode="wait">
        {!isCollapsed ? (
          <motion.div
            key={`expanded-logo-${logoKey}`} // Add key for force re-render
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            className="flex items-center justify-center"
          >
            <div className="relative group">
              {logoLoading && (
                <div className="h-12 w-12 bg-white/20 rounded-lg animate-pulse" />
              )}
              <img
                key={`logo-img-${logoKey}`} // Force re-render of img element
                src={logoUrl}
                alt={`${companyName} Logo`}
                className={`h-12 w-auto max-w-[160px] object-contain transition-all duration-300 group-hover:scale-105 ${
                  logoLoading ? "opacity-0 absolute" : "opacity-100"
                }`}
                style={{
                  filter:
                    visualIdentity?.logo || tenantData?.value?.logo
                      ? "none"
                      : "brightness(0) invert(1)",
                }}
                onLoad={handleLogoLoad}
                onError={handleLogoError}
              />
            </div>
          </motion.div>
        ) : (
          <motion.div
            key={`collapsed-logo-${logoKey}`} // Add key for force re-render
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            className="relative group"
          >
            <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
              {logoLoading ? (
                <div className="w-6 h-6 bg-white/30 rounded animate-pulse" />
              ) : (
                <img
                  key={`logo-img-collapsed-${logoKey}`} // Force re-render of img element
                  src={logoUrl}
                  alt={`${companyName} Logo`}
                  className="h-6 w-6 object-contain"
                  style={{
                    filter:
                      visualIdentity?.logo || tenantData?.value?.logo
                        ? "none"
                        : "brightness(0) invert(1)",
                  }}
                  onError={handleLogoError}
                />
              )}
            </div>
            {/* Enhanced Tooltip */}
            <div className="absolute left-full ml-3 top-1/2 -translate-y-1/2 bg-gray-900/95 backdrop-blur-sm text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-200 z-50 pointer-events-none shadow-xl border border-gray-700">
              {companyName}
              <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-900/95"></div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
});

SidebarLogo.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
  user: PropTypes.object,
};

// Professional Menu Item Component
const MenuItem = memo(({ item, isActive, isCollapsed, primaryColor }) => {
  const Icon = item.icon;

  const activeStyles = {
    background: `linear-gradient(135deg, ${hexToRgba(
      primaryColor,
      0.25
    )} 0%, ${hexToRgba(primaryColor, 0.15)} 100%)`,
    borderLeft: `3px solid ${primaryColor}`,
    color: "#ffffff",
    fontWeight: "600",
    boxShadow: `0 4px 12px ${hexToRgba(primaryColor, 0.3)}`,
  };

  return (
    <motion.div layout transition={{ duration: 0.2 }} className="px-3">
      <NavLink
        to={item.link}
        className={({ isActive }) => `
          relative flex items-center gap-3 px-4 py-3.5 rounded-xl
          transition-all duration-300 group border-l-3 border-transparent
          ${
            isActive
              ? "text-white font-semibold shadow-lg backdrop-blur-sm"
              : "text-white/75 hover:text-white hover:bg-white/8 font-medium"
          }
          transform hover:translate-x-1 hover:shadow-lg
        `}
        style={({ isActive }) => (isActive ? activeStyles : {})}
      >
        <div className="flex items-center gap-3 w-full">
          <div
            className={`flex-shrink-0 ${
              isCollapsed ? "w-6 h-6" : "w-5 h-5"
            } flex items-center justify-center`}
          >
            {React.createElement(Icon, {
              className: `${
                isCollapsed ? "text-lg" : "text-base"
              } transition-transform duration-200 group-hover:scale-105`,
            })}
          </div>

          <AnimatePresence>
            {!isCollapsed && (
              <motion.span
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
                className="text-sm font-medium truncate"
              >
                {item.label}
              </motion.span>
            )}
          </AnimatePresence>
        </div>

        {/* Enhanced Tooltip when collapsed */}
        {isCollapsed && (
          <div className="absolute left-full ml-3 top-1/2 -translate-y-1/2 bg-gray-900/95 backdrop-blur-sm text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-200 z-50 pointer-events-none shadow-xl border border-gray-700">
            {item.label}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-900/95"></div>
          </div>
        )}
      </NavLink>
    </motion.div>
  );
});

MenuItem.propTypes = {
  item: PropTypes.object.isRequired,
  isActive: PropTypes.bool.isRequired,
  isCollapsed: PropTypes.bool.isRequired,
  primaryColor: PropTypes.string.isRequired,
};

// Professional User Profile Component
const UserProfile = memo(({ user, isCollapsed }) => {
  const navigate = useNavigate();
  // const dispatch = useDispatch();

  const userInfo = useMemo(
    () => ({
      /*  name:
        user?.firstName && user?.lastName
          ? `${user.firstName} ${user.lastName}`
          : user?.username || "User", */
      email: user?.email || "",
      avatar: user?.avatar || DEFAULT_AVATAR,
      role: user?.role || "User",
      initials:
        user?.firstName && user?.lastName
          ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
          : (user?.username || "U")[0].toUpperCase(),
    }),
    [user]
  );

  const goProfile = useCallback(() => {
    const role = user?.role;
    const path = role === ROLES.ADMIN ? "/admin/profile" : "/user/profile";
    navigate(path);
  }, [navigate, user?.role]);

  if (isCollapsed) {
    return (
      <motion.div
        className="relative px-3 py-4"
        layout
        transition={{ duration: 0.3 }}
      >
        <div className="relative group">
          <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-white/30 cursor-pointer bg-white/10 backdrop-blur-sm hover:ring-white/50 transition-all duration-300">
            <img
              src={userInfo.avatar}
              alt={userInfo.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
            <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm hidden">
              {userInfo.initials}
            </div>
          </div>
          <div className="absolute left-full ml-3 top-1/2 -translate-y-1/2 bg-gray-900/95 backdrop-blur-sm text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-200 z-50 pointer-events-none shadow-xl border border-gray-700">
            <div className="font-semibold">{userInfo.name}</div>
            <div className="text-xs text-gray-300">{userInfo.role}</div>
            <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-900/95"></div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="relative px-4 py-4 border-t border-white/10 bg-white/5 backdrop-blur-sm"
      layout
      transition={{ duration: 0.3 }}
    >
      <div
        className="flex items-center gap-3 p-3 rounded-xl bg-white/10 hover:bg-white/15 transition-all duration-300 cursor-pointer border border-white/10 hover:border-white/20 backdrop-blur-sm"
        onClick={goProfile}
      >
        <div className="relative">
          <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-white/30">
            <img
              src={userInfo.avatar}
              alt={userInfo.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
            <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm hidden">
              {userInfo.initials}
            </div>
          </div>
          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold text-sm truncate">
            {userInfo.name}
          </div>
          <div className="text-white text-xs truncate">{userInfo.email}</div>
          <div className="text-white text-xs font-medium mt-0.5">
            {userInfo.role}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-white/60" />
      </div>
    </motion.div>
  );
});

UserProfile.propTypes = {
  user: PropTypes.object,
  isCollapsed: PropTypes.bool.isRequired,
};

// Main Sidebar Component
const Sidebar = ({ isCollapsed, onToggle, isMobile }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const user = useSelector(selectUser);
  const userRole = useSelector(selectUserRole);

  // **RBAC: Enhanced Permission-Based Access Control with Token-Based Modules**
  const auth = useAuth();
  const {
    permissions,
    modules: tenantModules, // Use token-based modules
  } = auth;

  // Dinamik tema renklerini al
  const { primaryColor, secondaryColor } = useThemeContext();

  // Local state for mobile menu only
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // 🚀 UNIFIED MENU FILTERING - Single Source of Truth
  const menuItems = useMemo(() => {
    return ALL_MENU_ITEMS
      // 1️⃣ ROL FİLTRELEMESİ: Kullanıcının rolüne uygun menüleri seç
      .filter(item => item.roles.includes(userRole))
      
      // 2️⃣ YETKİ FİLTRELEMESİ: Modül ve permission kontrolü
      .filter(item => {
        // Dashboard her zaman göster (requiredModule yok)
        if (!item.requiredModule) {
          return true;
        }

        // Admin her şeyi görebilir (sadece rol filtresi yeterli)
        if (userRole === ROLES.ADMIN) {
          return true;
        }

        // canAccessRoute ile detaylı kontrol
        const canShow = auth.canAccessRoute({
          allowedRoles: item.roles,
          requiredModule: item.requiredModule,
          requiredPermissions: item.requiredPermissions || [],
          requireAny: item.requireAny || false,
        });

        return canShow;
      })
      
      // 3️⃣ SIRA VE FORMAT: Link ve label formatla
      .sort((a, b) => a.order - b.order)
      .map(item => {
        // Resolve icon from MODULE_DEFINITIONS based on requiredModule or fallback to item id (e.g., dashboard)
        const moduleKey = item.requiredModule || item.id;
        const moduleIcon = MODULE_DEFINITIONS[moduleKey]?.icon;
        return {
          ...item,
          icon: moduleIcon || item.icon,
          // Admin ise adminLink kullan, yoksa normal link
          link: userRole === ROLES.ADMIN && item.adminLink ? item.adminLink : item.link,
          label: t(`sidebar.menu.${item.label}`),
        };
      });

  }, [userRole, t, auth, permissions, tenantModules]);

  // Professional sidebar background with solid primary color
  const sidebarStyles = useMemo(() => {
    return {
      backgroundColor: primaryColor, // Solid color, no gradient
      width: isMobile
        ? isMobileOpen
          ? "280px"
          : "0px"
        : isCollapsed
        ? `${SIDEBAR_WIDTH.COLLAPSED}px`
        : `${SIDEBAR_WIDTH.EXPANDED}px`,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    };
  }, [primaryColor, isMobile, isMobileOpen, isCollapsed]);

  // Handlers
  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setIsMobileOpen(!isMobileOpen);
    } else {
      onToggle();
    }
  }, [isMobile, isMobileOpen, onToggle]);

  const closeMobile = useCallback(() => {
    if (isMobile) {
      setIsMobileOpen(false);
    }
  }, [isMobile]);

  // Close mobile menu on route change
  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  if (!user) return null;

  return (
    <>
      {/* Mobile Toggle Button */}
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="fixed top-4 left-4 z-50 lg:hidden p-2 rounded-lg bg-white shadow-lg border border-gray-200 hover:bg-gray-50 transition-all duration-300 hover:scale-105"
          style={{ color: primaryColor }}
        >
          {isMobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      )}

      {/* Mobile Overlay */}
      <AnimatePresence>
        {isMobile && isMobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 lg:hidden"
            onClick={closeMobile}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        className="fixed left-0 top-0 h-full z-40 lg:relative lg:z-auto shadow-2xl lg:shadow-xl overflow-x-hidden"
        style={sidebarStyles}
        animate={{
          width: isMobile
            ? isMobileOpen
              ? SIDEBAR_WIDTH.EXPANDED
              : 0
            : isCollapsed
            ? SIDEBAR_WIDTH.COLLAPSED
            : SIDEBAR_WIDTH.EXPANDED,
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        <div className="relative h-full flex flex-col overflow-hidden">
          {/* Logo */}
          <SidebarLogo isCollapsed={isCollapsed && !isMobile} user={user} />

          {/* Navigation */}
          <nav className="flex-1 py-6 overflow-y-auto overflow-x-hidden relative custom-scrollbar overscroll-contain">
            <div className="space-y-2">
              {menuItems.map((item) => (
                <MenuItem
                  key={item.link}
                  item={item}
                  isActive={location.pathname === item.link}
                  isCollapsed={isCollapsed && !isMobile}
                  primaryColor={primaryColor}
                />
              ))}
            </div>
          </nav>

          <FooterBranding
            isCollapsed={isCollapsed && !isMobile}
            isMobile={isMobile}
            primaryColor={primaryColor}
          />
          {/* User Profile */}
          <UserProfile user={user} isCollapsed={isCollapsed && !isMobile} />
        </div>
      </motion.aside>
    </>
  );
};

Sidebar.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
};

// Enhanced Toggle Button Component (will be used in Header)
export const SidebarToggleButton = memo(
  ({ isCollapsed, onToggle, primaryColor = "#1E40AF" }) => (
    <motion.button
      onClick={onToggle}
      className="flex items-center justify-center w-10 h-10 rounded-xl bg-white shadow-lg border border-gray-200 hover:bg-gray-50 transition-all duration-300 hover:scale-105 hover:shadow-xl"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      style={{ color: primaryColor }}
    >
      {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
    </motion.button>
  )
);

SidebarToggleButton.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  primaryColor: PropTypes.string,
};

export default memo(Sidebar);
